export type Priority = 'low' | 'medium' | 'high' | 'critical';
export type Visibility = 'show-active' | 'show-all' | 'hide-all';

/**
 * A non-hierarchical reference from one node to another (e.g. a dependency).
 * Reserved for a future cross-task links feature: the tree structure lives in
 * `children`, while arbitrary node-to-node relations live here. `type` is an
 * open string ("depends-on", "blocks", "related", ...) so new relation kinds
 * never require a storage-format version bump.
 */
export interface TodoLink {
  to: string; // UUID of the referenced node
  type: string;
}

export interface TodoItem {
  name: string;
  children: string[]; // Array of UUIDs
  archived: boolean;
  done: boolean;
  blocked: boolean;
  priority: Priority;
  visibility: Visibility;
  description: string;
  highlighted: boolean;
  yPosition?: number; // For layout positioning
  timeEstimate?: number; // Time estimate in hours (for leaf nodes only)
  links?: TodoLink[]; // Optional cross-node references (reserved; not yet edited in-app)
}

export interface TodoTree {
  [id: string]: TodoItem;
}

/**
 * The set of tasks committed to for a single day.
 *
 * `date` is a local calendar date (YYYY-MM-DD) marking which day the plan was
 * made for; `items` is an ordered list of node ids where position carries the
 * day's ranking (top = do first). An empty `date` means "never planned".
 */
export interface TodayList {
  date: string;
  items: string[];
}

export const DEFAULT_TODO_ITEM: Omit<TodoItem, 'name'> = {
  children: [],
  archived: false,
  done: false,
  blocked: false,
  priority: 'medium',
  visibility: 'show-active',
  description: '',
  highlighted: false,
  timeEstimate: undefined,
};

export const ROOT_NODE_NAME = 'Everything';

export function isTodoTree(data: any): data is TodoTree {
  if (!data || typeof data !== 'object') {
    return false;
  }

  const keys = Object.keys(data);
  if (keys.length === 0) {
    return false;
  }

  for (const key of keys) {
    const item = data[key];

    if (!item || typeof item !== 'object') {
      return false;
    }

    // Only require 'name' field - all others get defaults
    if (!('name' in item) || typeof item.name !== 'string') {
      return false;
    }

    // Type-check fields that ARE present
    if ('children' in item && !Array.isArray(item.children)) {
      return false;
    }

    if ('archived' in item && typeof item.archived !== 'boolean') {
      return false;
    }

    if ('done' in item && typeof item.done !== 'boolean') {
      return false;
    }

    if ('blocked' in item && typeof item.blocked !== 'boolean') {
      return false;
    }

    if ('highlighted' in item && typeof item.highlighted !== 'boolean') {
      return false;
    }

    if ('description' in item && typeof item.description !== 'string') {
      return false;
    }

    if ('priority' in item && !['low', 'medium', 'high', 'critical'].includes(item.priority)) {
      return false;
    }

    if ('visibility' in item && !['show-active', 'show-all', 'hide-all'].includes(item.visibility)) {
      return false;
    }

    if ('timeEstimate' in item && item.timeEstimate !== undefined && (typeof item.timeEstimate !== 'number' || item.timeEstimate < 0)) {
      return false;
    }

  }

  return true;
}

export function hasTodoTreeRoot(tree: TodoTree): boolean {
  const rootNodes = Object.values(tree).filter(item => item.name === ROOT_NODE_NAME);
  return rootNodes.length === 1;
}

export function findRootNodeId(tree: TodoTree): string | null {
  for (const [id, item] of Object.entries(tree)) {
    if (item.name === ROOT_NODE_NAME) {
      return id;
    }
  }
  return null;
}

export function validateTodoTree(data: any): { isValid: boolean; error?: string } {
  if (!isTodoTree(data)) {
    return { isValid: false, error: 'Invalid todo tree structure' };
  }

  if (!hasTodoTreeRoot(data)) {
    return { isValid: false, error: 'Todo tree must have exactly one root node named "Everything"' };
  }

  const allUuids = new Set(Object.keys(data));

  for (const [, item] of Object.entries(data)) {
    for (const childId of item.children) {
      if (!allUuids.has(childId)) {
        return {
          isValid: false,
          error: `Item "${item.name}" references non-existent child: ${childId}`
        };
      }
    }
  }

  return { isValid: true };
}

// Nested JSON format types for Stage 3.3
export interface NestedTodoItem {
  name: string;
  children: NestedTodoNode[];
  archived: boolean;
  done: boolean;
  blocked: boolean;
  priority: Priority;
  visibility: Visibility;
  description: string;
  highlighted: boolean;
  timeEstimate?: number;
}

export interface NestedTodoNode {
  [id: string]: NestedTodoItem;
}

export type NestedTodoTree = NestedTodoNode;

export function isNestedTodoTree(data: any): data is NestedTodoTree {
  if (!data || typeof data !== 'object') {
    return false;
  }

  const keys = Object.keys(data);
  if (keys.length !== 1) {
    return false; // Nested format should have exactly one root
  }

  const rootKey = keys[0];
  const rootItem = data[rootKey];

  if (!rootItem || typeof rootItem !== 'object') {
    return false;
  }

  return validateNestedTodoItem(rootItem);
}

function validateNestedTodoItem(item: any): boolean {
  if (!item || typeof item !== 'object') {
    return false;
  }

  // Only require 'name' field - all others get defaults
  if (!('name' in item) || typeof item.name !== 'string') {
    return false;
  }

  // Type-check fields that ARE present
  if ('children' in item && !Array.isArray(item.children)) {
    return false;
  }

  if ('archived' in item && typeof item.archived !== 'boolean') {
    return false;
  }

  if ('done' in item && typeof item.done !== 'boolean') {
    return false;
  }

  if ('blocked' in item && typeof item.blocked !== 'boolean') {
    return false;
  }

  if ('highlighted' in item && typeof item.highlighted !== 'boolean') {
    return false;
  }

  if ('description' in item && typeof item.description !== 'string') {
    return false;
  }

  if ('priority' in item && !['low', 'medium', 'high', 'critical'].includes(item.priority)) {
    return false;
  }

  if ('visibility' in item && !['show-active', 'show-all', 'hide-all'].includes(item.visibility)) {
    return false;
  }

  // Backward compatibility: Accept old visibility format
  if ('showAllChildren' in item && typeof item.showAllChildren !== 'boolean') {
    return false;
  }

  if ('hideAllChildren' in item && typeof item.hideAllChildren !== 'boolean') {
    return false;
  }

  if ('timeEstimate' in item && item.timeEstimate !== undefined && (typeof item.timeEstimate !== 'number' || item.timeEstimate < 0)) {
    return false;
  }

  // Recursively validate children
  const children = item.children || [];
  for (const child of children) {
    if (!child || typeof child !== 'object') {
      return false;
    }

    const childKeys = Object.keys(child);
    if (childKeys.length !== 1) {
      return false;
    }

    const childItem = child[childKeys[0]];
    if (!validateNestedTodoItem(childItem)) {
      return false;
    }
  }

  return true;
}

export function validateNestedTodoTree(data: any): { isValid: boolean; error?: string } {
  if (!isNestedTodoTree(data)) {
    return { isValid: false, error: 'Invalid nested todo tree structure' };
  }

  const rootKey = Object.keys(data)[0];
  const rootItem = data[rootKey];

  if (rootItem.name !== ROOT_NODE_NAME) {
    return { isValid: false, error: 'Nested todo tree must have root node named "Everything"' };
  }

  return { isValid: true };
}